import type {
  ScorecardWorkbenchBaseline,
  ScorecardWorkbenchSessionSummary,
} from "../../../shared/scorecard-workbench";

/**
 * Session identity and Scorecard content live here and nowhere else: no URL,
 * no router state, no storage, no query cache. A refresh drops this object and
 * the page is back at the picker.
 */
export interface ScorecardWorkbenchState {
  selected: ScorecardWorkbenchSessionSummary | null;
  baseline: ScorecardWorkbenchBaseline | null;
  loading: boolean;
  error: string | null;
}

export function createScorecardWorkbenchState(): ScorecardWorkbenchState {
  return { selected: null, baseline: null, loading: false, error: null };
}

export type ScorecardWorkbenchAction =
  | { type: "select"; session: ScorecardWorkbenchSessionSummary }
  | { type: "baseline"; sessionId: string; baseline: ScorecardWorkbenchBaseline }
  | { type: "baseline_failed"; sessionId: string; message: string }
  | { type: "clear" };

function isStale(state: ScorecardWorkbenchState, sessionId: string): boolean {
  return state.selected?.sessionId !== sessionId;
}

export function scorecardWorkbenchReducer(
  state: ScorecardWorkbenchState,
  action: ScorecardWorkbenchAction
): ScorecardWorkbenchState {
  switch (action.type) {
    case "select":
      return { selected: action.session, baseline: null, loading: true, error: null };
    case "baseline":
      if (isStale(state, action.sessionId)) return state;

      return { ...state, baseline: action.baseline, loading: false, error: null };
    case "baseline_failed":
      if (isStale(state, action.sessionId)) return state;

      return { ...state, baseline: null, loading: false, error: action.message };
    case "clear":
      return createScorecardWorkbenchState();
  }
}

/** Generation needs a Scorecard to compare against, so it waits for one. */
export function canGenerate(state: ScorecardWorkbenchState): boolean {
  return state.baseline?.status === "ready";
}

export function generationBlockedReason(state: ScorecardWorkbenchState): string | null {
  if (!state.selected) return "Pick a Session first.";

  if (state.loading) return "Loading the baseline Scorecard.";

  if (state.baseline?.status === "ready") return null;

  return "Generation needs a completed baseline.";
}

/**
 * An id typed into the picker is a Session we know nothing else about; the
 * list row, when there is one, carries the client, RD and date.
 */
export function summaryForTypedId(
  sessionId: string,
  known: readonly ScorecardWorkbenchSessionSummary[]
): ScorecardWorkbenchSessionSummary {
  const listed = known.find((session) => session.sessionId === sessionId);
  if (listed) return listed;

  return {
    sessionId,
    clientName: "",
    rdName: "",
    sessionType: "follow-up",
    sessionDate: "",
    analysisStatus: "unknown",
    baselineReady: false,
  };
}
